import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { canReply, defaultFrom, fromOptions, replyBlockedReason } from './inboxRules';
import { useComposeFromInbox, useInboxes } from '../../hooks/useUnifiedInbox';
import { useAuth } from '../../context/AuthContext';
import { useEmailTemplates, useSignatures } from '../../hooks/useInbox';
import MailComposer from './MailComposer';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import ComposeSurface from './ComposeSurface';
import { MAIL_COMPOSE_TOOLBAR } from './editorPresets';
import { buildSignatureHtml, hasAuthoredText, withoutSignatureBlocks } from '../../lib/editorSafeHtml';
import { pickSeedSignature } from '../../lib/signatureSelection';
import { useAttachmentUpload } from '../../hooks/useAttachmentUpload';
import { useComposerDraft } from '../../hooks/useComposerDraft';
import { useInboxSenders } from '../../hooks/useUnifiedInbox';

export default function ComposePage() {
    const { inboxId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user, getAuthHeader } = useAuth();
    const { data: inboxes } = useInboxes();
    const inbox = useMemo(() => (inboxes || []).find(i => i.inbox_id === inboxId), [inboxes, inboxId]);
    const compose = useComposeFromInbox(inboxId);
    const { data: templatesData } = useEmailTemplates();
    const { data: signaturesData } = useSignatures();
    // #597 — org masters were never read here: this page showed neither org
    // templates nor org signatures, and resolved no merge variables at all.
    const templates = templatesData?.templates || [];
    const adminTemplates = templatesData?.adminTemplates || [];
    const signatures = signaturesData?.signatures || [];
    const adminSignatures = signaturesData?.adminSignatures || [];
    const defaultSignature = signaturesData?.defaultSignature || null;

    const [to, setTo] = useState([]);
    const [cc, setCc] = useState([]);
    const [bcc, setBcc] = useState([]);
    // Two booleans, not one: the design reveals Cc and Bcc independently.
    // Declared with the other hooks, ABOVE the `!inbox` / `!mayCompose`
    // early returns further down — a hook after one of those is a
    // rules-of-hooks error and breaks the build, not a warning.
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [reconnectRequired, setReconnectRequired] = useState(false);
    // #597 — { id, html } of the signature currently in the body; the shared
    // TemplateSignatureSelectors owns the insert/replace/remove behaviour, so
    // the local applyTemplate/insertSignature below are gone.
    const [signature, setSignature] = useState({ id: '', html: '' });
    // #623 — attachments are composer state, but the SEND path needs them, and
    // the send path lives here. Same ownership rule as the signature.
    const attachmentState = useAttachmentUpload();
    const handleAddAttachments = attachmentState.addAttachments;

    // Draft auto-save key (declared with state for clarity)

    // ownAddress MUST be declared before fromAddress's useState lazy
    // initializer — otherwise it's a TDZ violation that fires in production
    // minified builds ("Cannot access 'L' before initialization").
    const ownAddress = user?.email;
    const [fromAddress, setFromAddress] = useState(() => {
        // Initialize synchronously to avoid "Sending as null" flash
        const opts = fromOptions(inbox, user, { ownAddress });
        return opts.length ? defaultFrom(opts) : null;
    });
    // The server's answer narrows the pool — see `useInboxSenders`. The
    // initialiser above deliberately does NOT wait for it: a first paint
    // showing "Sending as null" is worse than one option too many for a
    // moment, and the effect below re-picks once the answer lands.
    const { sendableAddresses } = useInboxSenders(inboxId);
    const options = useMemo(
        () => fromOptions(inbox, user, { ownAddress, sendableAddresses }),
        [inbox, user, ownAddress, sendableAddresses],
    );
    // If the address chosen before the server answered is not one it allows,
    // fall back to one it does — otherwise Send fails on an identity the
    // picker no longer shows.
    useEffect(() => {
        if (!sendableAddresses || !options.length) return;
        if (options.some((o) => o.address === fromAddress)) return;
        setFromAddress(defaultFrom(options));
    }, [sendableAddresses, options, fromAddress]);
    const mayCompose = canReply(inbox, user, { ownAddress });

    // Pre-select the From address based on the inbox's recipient address
    // (Gmail pattern — when composing from an inbox, default to the inbox's own address)
    useEffect(() => {
        if (!options.length) return;
        const inboxAddr = inbox?.distro_smtp || inbox?.member_smtps?.[0];
        const inboxMatch = options.find(opt => opt.address === inboxAddr);
        setFromAddress(inboxMatch ? inboxMatch.address : defaultFrom(options));
    }, [options, inbox]);

    // F07: forwarded content and the seeded signature are handled in ONE effect
    // so they cannot race each other.
    //
    // #597 fixes two defects here:
    //  1. the old `else if` meant a forwarded body SUPPRESSED the signature
    //     entirely, so every forward went out unsigned — one of the eight
    //     mode × inbox combinations this issue exists to cover;
    //  2. the signature was inserted RAW (`defaultSignature.content`), with no
    //     merge-variable resolution at all, so a forward or compose from this
    //     page shipped a literal `{{SenderName}}` to the recipient.
    useEffect(() => {
        const fwdSubject = location.state?.forwardedSubject || searchParams.get('subject');
        // #970 — a forwarded message sent from this CRM carries the original
        // sender's signature block; unmark it so it stays quoted content.
        const fwdBody = withoutSignatureBlocks(location.state?.forwardedBody || searchParams.get('body'));
        if (fwdSubject) setSubject(fwdSubject);

        const seed = pickSeedSignature({
            activeOverrideId: signaturesData?.activeOverrideId,
            signatures: signaturesData?.signatures,
            adminSignatures: signaturesData?.adminSignatures,
            defaultSignature,
        });
        // The SAME recipe TemplateSignatureSelectors uses, so its strip matches.
        const sigHtml = seed ? buildSignatureHtml(seed, user, fromAddress || '') : '';
        if (sigHtml) setSignature({ id: seed.signature_id, html: sigHtml });

        if (fwdBody) {
            // The note goes ABOVE the forwarded thread, the signature below it —
            // and the forwarded thread itself is never touched.
            setBody(sigHtml ? `<p></p><p></p>${sigHtml}<p><br></p>${fwdBody}` : fwdBody);
        } else {
            setBody(prev => (prev ? prev : (sigHtml ? `<p></p><p></p>${sigHtml}` : '')));
        }
    }, [location.state, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const send = async () => {
        if (!to.length) { toast.error('Please add at least one recipient'); return; }
        if (!subject.trim()) { toast.error('Subject required'); return; }
        if (!hasAuthoredText(body)) { toast.error('Type a message first'); return; }
        // #623 — never send while an upload is in flight or has failed. The
        // whole point of surfacing a failure in the composer is that the user
        // does not discover it after believing the message went out.
        if (attachmentState.pendingCount) { toast.error('Wait for attachments to finish uploading'); return; }
        if (attachmentState.failedCount) { toast.error('Remove or retry the attachments that failed to upload'); return; }
        try {
            await compose.mutateAsync({
                to: to.map(c => c.email),
                cc: cc.map(c => c.email),
                bcc: bcc.map(c => c.email), // UX-B2 fix: Actually send BCC recipients
                subject,
                body,
                body_type: 'HTML',
                from_address: fromAddress,
                // Staged object keys, not bytes — the server fetches them.
                ...(attachmentState.payload.length && { attachments: attachmentState.payload }),
            });
            toast.success(`Email sent as ${fromAddress}`);
            // UX-3: Clear draft on successful send
            draft.clear();
            navigate(`/inbox?inbox=${inboxId}`);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;

            // 502/503 = server temporarily unavailable (Graph API issue, not user error)
            if (status === 502 || status === 503) {
                toast.error(
                    'The mail server is temporarily unavailable. Please wait a moment and try again.',
                    { autoClose: 8000 }
                );
                return; // Keep draft so user can retry
            }

            // 504 = timeout
            if (status === 504 || detail === 'send_timeout') {
                toast.error('The email server took too long to respond. Please try again.');
                return;
            }

            if (detail === 'reconnect_required') {
                setReconnectRequired(true);
                toast.error('Your mailbox connection has expired. Please re-authorize in Settings to send emails.');
                return;
            }
            toast.error(
                detail === 'from_address_denied' ? 'You may not send from that address'
                    : detail === 'recipient_required' ? 'Please add at least one recipient'
                    : detail === 'send_failed' ? 'Failed to send email. The mail server may be busy — please try again in a moment.'
                    : detail || 'Failed to send email. Please try again.'
            );
        }
    };

    /**
     * UX-3 / #623 — draft autosave, now via the shared `useComposerDraft` hook.
     *
     * The debounce, the dirty flag, the localStorage key shape and the
     * beforeunload guard were duplicated byte-for-byte here and in
     * PersonalComposePage; they live in one place now, and the hook adds the
     * visible "Draft saved at HH:MM" and the Discard confirmation #623 asks for.
     * Drafts remain CLIENT-SIDE — there are no draft endpoints in this backend.
     */
    // `attachmentState.payload` is in here so a draft keeps its files. It was
    // omitted, so every autosave wrote `attachments: []` over whatever the
    // user had attached.
    const draftAttachments = attachmentState.payload;
    const draftValue = useMemo(
        () => ({ to, cc, bcc, subject, body, fromAddress, attachments: draftAttachments }),
        [to, cc, bcc, subject, body, fromAddress, draftAttachments],
    );
    const hasPrefill = Boolean(
        location.state?.forwardedSubject || location.state?.forwardedBody
        || searchParams.get('subject') || searchParams.get('body'),
    );
    const reopenedDraftId = location.state?.draft?.draft_id || null;
    const draft = useComposerDraft(draftValue, {
        enabled: !hasPrefill, getAuthHeader, inboxId: inboxId, draftId: reopenedDraftId,
    });

    // F08: never restore over forwarded content — it is what the user asked for.
useEffect(() => {
        // Compose opens EMPTY. It used to restore whatever was in
        // localStorage, so a new message started with the last one's
        // contents — the reported "compose should always be a new compose
        // state". A draft is now reopened only by clicking it in the Drafts
        // view, which hands it over here in router state: an explicit act
        // rather than a surprise.
        const reopened = location.state?.draft;
        if (!reopened) return;
        if (reopened.to) setTo(reopened.to.map((email) => ({ email })));
        if (reopened.cc) setCc(reopened.cc.map((email) => ({ email })));
        if (reopened.bcc) setBcc(reopened.bcc.map((email) => ({ email })));
        if (reopened.subject) setSubject(reopened.subject);
        if (reopened.body) setBody(reopened.body);
        // The attachments come back with it. They are already in S3 (the
        // draft stored the refs), so they enter `ready` and the send path
        // picks them up unchanged — see `useAttachments.restore`.
        if (reopened.attachments?.length) attachmentState.restore(reopened.attachments);
        // #597/D14 — a reopened draft WINS over the signature seed, and we
        // cannot know which signature (if any) is inside it. An empty tracked
        // value makes the next switch append once rather than strip the wrong
        // thing — the conservative choice when the body's provenance is
        // unknown.
        draftRestored.current = true;
        setSignature({ id: '', html: '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // While inboxes are still loading, show a spinner instead of falling
    // through to the permission-denied path (canReply returns false when
    // inbox is undefined, which is indistinguishable from a real denial
    // until the data resolves).
    if (!inbox) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    if (!mayCompose) {
        return (
            <div className="flex flex-col items-center justify-center h-full py-20">
                <p className="text-stone-400">{replyBlockedReason(inbox, user, { ownAddress }) || "You don't have permission to compose from this inbox."}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate(`/inbox?inbox=${inboxId}`)}>
                    Back to inbox
                </Button>
            </div>
        );
    }

    const backToInbox = () => {
        // M-Navigation: prefer history back, fall back to the inbox URL with
        // the filters preserved.
        const backParams = new URLSearchParams();
        backParams.set('inbox', searchParams.get('inbox') || inboxId);
        ['search', 'sender', 'date_from', 'date_to', 'replied', 'sort', 'dir', 'page'].forEach(key => {
            const val = searchParams.get(key);
            if (val) backParams.set(key, val);
        });
        if (window.history.state && window.history.state.idx > 0) navigate(-1);
        else navigate(`/inbox?${backParams.toString()}`);
    };

    const leave = () => {
        if (draft.isDirty && !window.confirm('You have unsaved changes. Discard and leave?')) return;
        backToInbox();
    };

    /**
     * Discard: delete the draft, then LEAVE.
     *
     * The callback reset the fields and stopped there, so the draft went and
     * the user stayed on the compose page looking at an empty composer —
     * which reads as "a new draft has started", the opposite of what they
     * asked for (#708). `backToInbox` rather than `leave`: `leave` re-checks
     * `draft.isDirty`, which has not re-rendered yet, and would ask a second
     * time about a draft already deleted.
     *
     * The field reset stays even though the page unmounts — a stale value
     * restored into the next compose is the failure #694 is about.
     */
    const discard = () => draft.discard(() => {
        setTo([]); setCc([]); setBcc([]); setSubject(''); setBody('');
        setShowCc(false); setShowBcc(false);
        setSignature({ id: '', html: '' });
        attachmentState.reset();
        backToInbox();
    });

    /** Discard asks first, unless there is nothing to lose. */
    const requestDiscard = () => {
        if (draft.hasDraftContent) setConfirmDiscard(true);
        else discard();
    };

    return (
        <>
        <ComposeSurface
            testId="compose-page"
            onSend={send}
            sending={compose.isPending}
            hasContent={hasAuthoredText(body)}
            sendTestId="compose-send-button"
            footerSendTestId="compose-send-footer"
            onDiscard={requestDiscard}
            discardTestId="compose-discard-button"
            onClose={leave}
            note={`Sending as ${fromAddress} · via Microsoft Graph`}
            noteTestId="compose-send-note"
        >
            {/* #623 — the shared composer, in `compose` mode; `page` variant so
                both lanes draw the same surface. The page keeps the send path
                and the navigation, which genuinely differ per lane. */}
            <MailComposer
                mode="compose"
                composerId="compose"
                variant="page"
                // Full-page compose keeps the merge-variable dropdown and
                // Content Library, which the reply preset omits. Without
                // this the page would silently lose both controls it
                // already passes `showMergeVariables`/`showContentLibrary`
                // for.
                toolbar={MAIL_COMPOSE_TOOLBAR}
                reconnectRequired={reconnectRequired}
                options={options}
                from={fromAddress}
                onFromChange={setFromAddress}
                to={to}
                onToChange={setTo}
                cc={cc}
                onCcChange={setCc}
                bcc={bcc}
                onBccChange={setBcc}
                showCc={showCc}
                onToggleCc={setShowCc}
                showBcc={showBcc}
                onToggleBcc={setShowBcc}
                subject={subject}
                onSubjectChange={setSubject}
                body={body}
                onBodyChange={setBody}
                draftSavedAt={draft.savedAtLabel}
                showMergeVariables
                showAIWriter
                showContentLibrary
                templates={templates}
                adminTemplates={adminTemplates}
                signatures={signatures}
                adminSignatures={adminSignatures}
                activeOverrideId={signaturesData?.activeOverrideId}
                user={user}
                senderEmail={fromAddress || ''}
                ownDomain={fromAddress || ''}
                signature={signature}
                onSignatureChange={setSignature}
                attachments={attachmentState.attachments}
                onAddAttachments={handleAddAttachments}
                onRemoveAttachment={attachmentState.remove}
            />
        </ComposeSurface>

            <AlertDialog
                open={confirmDiscard}
                onOpenChange={(open) => !open && setConfirmDiscard(false)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard this draft?</AlertDialogTitle>
                        <AlertDialogDescription>
                            The draft is deleted and cannot be recovered. Close the
                            composer instead to keep it in Drafts.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep editing</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="compose-discard-confirm"
                            onClick={() => {
                                setConfirmDiscard(false);
                                discard();
                            }}
                        >
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
